import React, { useState } from 'react';
import './Login.css';
import logoImg from '../../assets/logo.png';

const Login = ({ onSwitchView, onLoginSuccess }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    rememberMe: false
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [isDeactivated, setIsDeactivated] = useState(false);
  const [deactivatedAccount, setDeactivatedAccount] = useState({ username: '', email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Secure card expand/retract state (retracted on mobile by default to focus on form, expanded on desktop)
  const [isSecureCardExpanded, setIsSecureCardExpanded] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth > 992;
    }
    return false;
  });

  const toggleSecureCard = () => {
    setIsSecureCardExpanded(prev => !prev);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errorMsg) {
      setErrorMsg('');
      setIsDeactivated(false);
      setIsSuspended(false);
      setDeactivatedAccount({ username: '', email: '' });
      setSuspendedDetails(null);
    }
  };

  const [isSuspended, setIsSuspended] = useState(false);
  const [suspendedDetails, setSuspendedDetails] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setIsDeactivated(false);
    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username: formData.username, password: formData.password }),
      });

      const data = await response.json();

      if (response.ok) {
        if (onLoginSuccess) {
          onLoginSuccess(data.user);
        }
      } else {
        const isDeact = data.status === 'deactivated' || (data.message && data.message.toLowerCase().includes('deactivated'));
        const isSusp = data.status === 'suspended' || (data.message && data.message.toLowerCase().includes('suspended'));
        setIsDeactivated(Boolean(isDeact));
        setIsSuspended(Boolean(isSusp));
        if (isDeact) {
          setDeactivatedAccount({
            username: data.username || formData.username || '',
            email: data.email || (formData.username.includes('@') ? formData.username : '')
          });
        }
        if (isSusp) {
          setSuspendedDetails({
            username: data.username || formData.username || '',
            email: data.email || (formData.username.includes('@') ? formData.username : ''),
            daysRemaining: data.daysRemaining || 60,
            endDate: data.endDate || 'In 2 months',
            reason: data.reason || 'Repeated no attendance'
          });
        }
        setErrorMsg(data.message || 'Login failed.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setErrorMsg('Failed to connect to the server.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="split-layout-container">
      {/* Left Panel */}
      <div className="left-panel">
        <div className="left-panel-top">
          <img src={logoImg} alt="BlackBerry" className="brand-logo" />
        </div>
        
        <div className="left-panel-middle">
           {/* Space reserved for any future illustration or welcome message */}
        </div>

        <div className="left-panel-bottom">
          <div className={`secure-registration-card ${isSecureCardExpanded ? 'expanded' : 'retracted'}`}>
            <div 
              className="secure-card-header" 
              onClick={toggleSecureCard}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleSecureCard();
                }
              }}
              role="button"
              tabIndex={0}
              aria-expanded={isSecureCardExpanded}
              aria-label={isSecureCardExpanded ? "Retract security information" : "Expand security information"}
            >
              <div className="secure-header-left">
                <div className="secure-icon-badge">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shield-icon-mini">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                    <polyline points="9 12 11 14 15 10"></polyline>
                  </svg>
                </div>
                <div className="secure-header-text">
                  <h3>Your Information is Safe</h3>
                  <span className="secure-status-tag">256-bit SSL Encrypted</span>
                </div>
              </div>
              <div className="secure-header-action">
                <span className="secure-toggle-hint">{isSecureCardExpanded ? 'Hide' : 'Details'}</span>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="18" 
                  height="18" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  className={`secure-card-chevron ${isSecureCardExpanded ? 'open' : ''}`}
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
            </div>

            <div className="secure-card-body">
              <div className="secure-icon-wrapper desktop-only-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shield-icon">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                  <polyline points="9 12 11 14 15 10"></polyline>
                </svg>
              </div>
              <p className="secure-card-description">
                Your credentials are encrypted and protected to ensure maximum privacy.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="right-panel" style={{ alignItems: 'center' }}>
        <div className="form-container" style={{ maxWidth: '420px', margin: '0 auto', width: '100%' }}>
          
          <div className="login-header-wrapper">
            <div className="login-title-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
            </div>
            <h2>Login</h2>
            <p>Enter your credentials to access your account</p>
          </div>

          {isSuspended && suspendedDetails ? (
            <div style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderLeft: '5px solid #dc2626',
              borderRadius: '8px',
              padding: '1rem 1.15rem',
              marginBottom: '1.5rem',
              textAlign: 'left'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap', gap: '6px' }}>
                <strong style={{ color: '#991b1b', fontSize: '0.95rem' }}>Account Suspended (2-Month Blackout)</strong>
                <span style={{
                  background: '#dc2626',
                  color: '#ffffff',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: '12px'
                }}>
                  {suspendedDetails.daysRemaining} days remaining
                </span>
              </div>
              <p style={{ margin: '0 0 8px 0', fontSize: '0.825rem', color: '#7f1d1d', lineHeight: 1.5 }}>
                Your learner account has been suspended for 2 months due to repeated attendance policy infractions (poor attendance &lt; 70% or no-shows).
              </p>
              <div style={{ background: '#ffffff', border: '1px solid #fee2e2', borderRadius: '6px', padding: '0.55rem 0.75rem', fontSize: '0.78rem', color: '#475569', marginBottom: '8px' }}>
                <div><strong>Suspension Ends:</strong> {suspendedDetails.endDate}</div>
                <div><strong>Reason:</strong> {suspendedDetails.reason}</div>
              </div>
              <p style={{ margin: 0, fontSize: '0.78rem', color: '#64748b' }}>
                Need an administrative review or appeal?{' '}
                <a
                  href={`mailto:ccoe-support@blackberry.local?subject=${encodeURIComponent(`Suspension Appeal - ${suspendedDetails.username || suspendedDetails.email}`)}&body=${encodeURIComponent(`Dear Administrator,\n\nI am requesting a review of my account suspension.\n\nAccount: ${suspendedDetails.username || suspendedDetails.email}\nDays Remaining: ${suspendedDetails.daysRemaining}\n\nThank you.`)}`}
                  style={{ color: '#4f46e5', fontWeight: 700, textDecoration: 'underline' }}
                >
                  Contact Administrator
                </a>
              </p>
            </div>
          ) : errorMsg && (
            <div style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#991b1b',
              padding: '0.85rem 1rem',
              borderRadius: '8px',
              marginBottom: '1.5rem',
              fontSize: '0.875rem',
              lineHeight: '1.5'
            }}>
              <span>{errorMsg}</span>
              {isDeactivated && (() => {
                const userVal = deactivatedAccount.username || formData.username || '';
                const emailVal = deactivatedAccount.email || (formData.username.includes('@') ? formData.username : '');
                const mailSubject = 'Account Reactivation Request';
                const mailBody = [
                  'Dear Administrator,',
                  '',
                  'I would like to request the reactivation of my account so that I can access my training courses on the BlackBerry CCoE platform.',
                  '',
                  'Here are my account details:',
                  `- Username: ${userVal}`,
                  `- Email: ${emailVal}`,
                  '',
                  'Thank you.'
                ].join('\r\n');

                return (
                  <a
                    href={`mailto:admin3@blackberry.com?subject=${encodeURIComponent(mailSubject)}&body=${encodeURIComponent(mailBody)}`}
                    style={{
                      color: '#4338ca',
                      fontWeight: '700',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      marginLeft: '6px'
                    }}
                  >
                    Email Us
                  </a>
                );
              })()}
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group full" style={{ gap: '0.5rem' }}>
              <label htmlFor="username" style={{ padding: 0 }}>Username / Email Address</label>
              <div className="input-with-icon">
                <svg className="input-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                <input 
                  type="text" 
                  id="username" 
                  name="username" 
                  value={formData.username} 
                  onChange={handleChange} 
                  required 
                  placeholder="Enter your username or email address" 
                />
              </div>
            </div>

            <div className="form-group full" style={{ gap: '0.5rem' }}>
              <label htmlFor="password" style={{ padding: 0 }}>Password</label>
              <div className="password-input-wrapper">
                <div className="input-with-icon" style={{ width: '100%' }}>
                  <svg className="input-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                  <input 
                    type={showPassword ? "text" : "password"} 
                    id="password" 
                    name="password" 
                    value={formData.password} 
                    onChange={handleChange} 
                    required 
                    placeholder="Enter your password" 
                    style={{ paddingRight: '2.5rem' }}
                    data-hj-suppress
                  />
                </div>
                <div className="eye-icon" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  )}
                </div>
              </div>
            </div>

            <div className="remember-forgot-row">
              <label className="custom-checkbox-wrapper" style={{ padding: 0, margin: 0, alignItems: 'center' }}>
                <input 
                  type="checkbox" 
                  name="rememberMe" 
                  checked={formData.rememberMe} 
                  onChange={handleChange} 
                  className="custom-checkbox" 
                  style={{ marginTop: 0 }}
                />
                <span className="checkbox-text" style={{ color: '#1e293b', fontWeight: 500 }}>Remember me</span>
              </label>
              
              <a href="#forgot" className="forgot-password-link" onClick={(e) => { e.preventDefault(); if(onSwitchView) onSwitchView('resetPassword'); }}>Forgot password?</a>
            </div>

            <button type="submit" className="login-submit-btn" disabled={isSubmitting}>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
              {isSubmitting ? 'Logging in...' : 'Login'}
            </button>
            
            <div className="divider">or</div>

            <div className="register-prompt">
              Don't have an account? <a href="#register" className="register-link" onClick={(e) => { e.preventDefault(); if(onSwitchView) onSwitchView('register'); }}>Register here</a>
            </div>
            
          </form>
        </div>
      </div>

    </div>
  );
};

export default Login;
